import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  LocalPharmacy as PharmacyIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment";
import PrescriptionDialog from "./PrescriptionDialog";

const PrescriptionsList = ({ patient, doctor, patientId: patientIdProp, ticketId }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [prescriptionDialogOpen, setPrescriptionDialogOpen] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const patientId = patientIdProp ?? patient?.id ?? patient;
  const doctorUser = doctor ?? auth?.user?.user;
  
  const {
    data: prescriptionsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["prescriptions", patientId],
    queryFn: () => {
      if (!patientId) {
        return Promise.resolve({ results: [] });
      }
      return axios
        .get(
          `${process.env.REACT_APP_API_URL}/prescriptions/?patient=${patientId}&page_size=100`,
          {
            headers: { Authorization: `Token ${token}` },
          }
        )
        .then((res) => {
          // Ensure we return filtered results
          const data = res.data;
          if (data.results) {
            // Filter to ensure only this patient's prescriptions
            const filtered = data.results.filter(
              (prescription) => {
                const prescPatientId = prescription.patient?.id || prescription.patient;
                return prescPatientId === patientId || prescPatientId === parseInt(patientId) || String(prescPatientId) === String(patientId);
              }
            );
            return { ...data, results: filtered };
          }
          // If it's an array, filter it
          if (Array.isArray(data)) {
            return {
              results: data.filter(
                (prescription) => {
                  const prescPatientId = prescription.patient?.id || prescription.patient;
                  return prescPatientId === patientId || prescPatientId === parseInt(patientId) || String(prescPatientId) === String(patientId);
                }
              ),
            };
          }
          return data;
        });
    },
    enabled: !!token && !!patientId,
  });

  const prescriptions = prescriptionsData?.results || prescriptionsData || [];

  const handleViewPrescription = (prescription) => {
    setSelectedPrescription(prescription);
    setViewDialogOpen(true);
  };

  return (
    <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">
          {t("ipd.admissions.prescriptions", "Prescriptions")}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setPrescriptionDialogOpen(true)}
        >
          {t("ipd.admissions.add_prescription", "Add Prescription")}
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : isError ? (
        <Alert severity="error">
          {error?.message ||
            t("ipd.admissions.error_loading_prescriptions", "Failed to load prescriptions.")}
        </Alert>
      ) : prescriptions.length === 0 ? (
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setPrescriptionDialogOpen(true)}
            >
              {t("ipd.admissions.add_first_prescription", "Add First Prescription")}
            </Button>
          }
        >
          {t("ipd.admissions.no_prescriptions", "No prescriptions found for this patient.")}
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("ipd.admissions.date", "Date")}</TableCell>
                <TableCell>{t("ipd.admissions.doctor", "Doctor")}</TableCell>
                <TableCell>{t("ipd.admissions.medicines_count", "Medicines")}</TableCell>
                <TableCell>{t("ipd.admissions.payment_status", "Payment Status")}</TableCell>
                <TableCell>{t("common.actions", "Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prescriptions.map((prescription) => (
                <TableRow key={prescription.id}>
                  <TableCell>
                    {moment(prescription.date).format("YYYY-MM-DD")}
                  </TableCell>
                  <TableCell>{prescription.doctor_display}</TableCell>
                  <TableCell>
                    {prescription.drugs?.length || 0}{" "}
                    {prescription.extra_medicines?.length > 0 &&
                      `+ ${prescription.extra_medicines.length} extra`}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        prescription.payment_status === 2
                          ? t("common.paid", "Paid")
                          : prescription.payment_status === 1
                          ? t("common.pending", "Pending")
                          : t("common.unpaid", "Unpaid")
                      }
                      size="small"
                      color={
                        prescription.payment_status === 2
                          ? "success"
                          : prescription.payment_status === 1
                          ? "warning"
                          : "error"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewPrescription(prescription)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PrescriptionDialog
        open={prescriptionDialogOpen}
        onClose={() => {
          setPrescriptionDialogOpen(false);
          refetch();
        }}
        patient={patient ?? (patientId != null ? { id: patientId } : undefined)}
        initialPatientId={patientId}
        ticketId={ticketId ?? null}
        doctor={doctorUser}
      />

      {/* View Prescription Dialog - prescription only (no price); printable */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("ipd.admissions.prescription_details", "Prescription Details")} #
          {selectedPrescription?.id}
        </DialogTitle>
        <DialogContent dividers>
          {selectedPrescription && (
            <Box id="prescription-view-content">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("ipd.admissions.date", "Date")}
                  </Typography>
                  <Typography variant="body1">
                    {moment(selectedPrescription.date).format("YYYY-MM-DD HH:mm")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("ipd.admissions.doctor", "Doctor")}
                  </Typography>
                  <Typography variant="body1">
                    {selectedPrescription.doctor_display}
                  </Typography>
                </Grid>
                {selectedPrescription.patient_display && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("ipd.admissions.patient", "Patient")}
                    </Typography>
                    <Typography variant="body1">
                      {selectedPrescription.patient_display}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: "bold" }}>
                    {t("ipd.admissions.medicines", "Medicines")}
                  </Typography>
                  {selectedPrescription.drugs?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: "grey.100" }}>
                            <TableCell><strong>{t("ipd.admissions.medicine", "Medicine")}</strong></TableCell>
                            <TableCell><strong>{t("ipd.admissions.dosage", "Dosage")}</strong></TableCell>
                            <TableCell><strong>{t("ipd.admissions.unitsPerMeal", "Units/Meal")}</strong></TableCell>
                            <TableCell><strong>{t("pharmacy.timesPer24", "Times/24h")}</strong></TableCell>
                            <TableCell><strong>{t("ipd.admissions.quantity", "Quantity")}</strong></TableCell>
                            <TableCell><strong>{t("ipd.admissions.timing", "When")}</strong></TableCell>
                            <TableCell><strong>{t("ipd.admissions.notation", "Notation")}</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedPrescription.drugs.map((drug, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                {drug.stock_medicine_display || drug.stock_medicine_name || drug.medication_name || "—"}
                              </TableCell>
                              <TableCell>{drug.dosage ?? "—"}</TableCell>
                              <TableCell>{drug.units_per_meal ?? "—"}</TableCell>
                              <TableCell>{drug.times_per_24 ?? "—"}</TableCell>
                              <TableCell>{drug.quantity ?? "—"}</TableCell>
                              <TableCell>
                                {drug.is_before === "before"
                                  ? (t("pharmacy.beforeMeal", "Before meal") || "Before meal")
                                  : drug.is_before === "med"
                                    ? (t("pharmacy.withMeal", "With meal") || "With meal")
                                    : (t("pharmacy.afterMeal", "After meal") || "After meal")}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 200 }}>{drug.notation || "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("ipd.admissions.no_medicines", "No medicines listed.")}
                    </Typography>
                  )}
                  {selectedPrescription.extra_medicines?.length > 0 && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: "bold", mt: 2 }}>
                        {t("ipd.admissions.extra_medicines", "Extra medicines")}
                      </Typography>
                      {selectedPrescription.extra_medicines.map((med, index) => (
                        <Paper key={`extra-${index}`} variant="outlined" sx={{ p: 2, mb: 1 }}>
                          <Typography variant="body2" fontWeight="bold">{med.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t("ipd.admissions.dosage", "Dosage")}: {med.dosage ?? "—"} · {t("ipd.admissions.quantity", "Quantity")}: {med.quantity ?? "—"}
                            {med.times_per_24 != null && med.times_per_24 !== "" ? ` · ${t("pharmacy.timesPer24", "Times/24h")}: ${med.times_per_24}` : ""}
                            {med.is_before ? ` · ${med.is_before}` : ""}
                          </Typography>
                          {med.notation && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {t("ipd.admissions.notation", "Notation")}: {med.notation}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            {t("common.close", "Close")}
          </Button>
          {selectedPrescription && (
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={() => {
                const p = selectedPrescription;
                const whenLabel = (isBefore) => {
                  if (isBefore === "before") return t("pharmacy.beforeMeal", "Before meal");
                  if (isBefore === "med") return t("pharmacy.withMeal", "With meal");
                  return t("pharmacy.afterMeal", "After meal");
                };
                const drugRows = (p.drugs || []).map(
                  (d) =>
                    `<tr><td>${d.stock_medicine_display || d.stock_medicine_name || "—"}</td><td>${d.dosage ?? "—"}</td><td>${d.units_per_meal ?? "—"}</td><td>${d.quantity ?? "—"}</td><td>${whenLabel(d.is_before)}</td><td>${d.notation || "—"}</td></tr>`
                ).join("");
                const title = `${t("ipd.admissions.prescription_details", "Prescription")} #${p.id}`;
                const win = window.open("", "_blank");
                win.document.write(`
                  <!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
                  <style>
                    body{font-family:Arial,sans-serif;padding:24px;max-width:800px;margin:0 auto;}
                    h1{font-size:1.25rem;margin-bottom:8px;}
                    .meta{margin-bottom:16px;color:#555;}
                    table{border-collapse:collapse;width:100%;margin-top:12px;}
                    th,td{border:1px solid #ddd;padding:8px 12px;text-align:left;}
                    th{background:#f5f5f5;}
                  </style></head><body>
                  <h1>${title}</h1>
                  <div class="meta">${moment(p.date).format("YYYY-MM-DD HH:mm")} · ${p.doctor_display || ""}${p.patient_display ? ` · ${p.patient_display}` : ""}</div>
                  <p><strong>${t("ipd.admissions.medicines", "Medicines")}</strong></p>
                  <table><thead><tr><th>${t("ipd.admissions.medicine", "Medicine")}</th><th>${t("ipd.admissions.dosage", "Dosage")}</th><th>${t("ipd.admissions.unitsPerMeal", "Units/Meal")}</th><th>${t("ipd.admissions.quantity", "Quantity")}</th><th>${t("ipd.admissions.timing", "When")}</th><th>${t("ipd.admissions.notation", "Notation")}</th></tr></thead><tbody>${drugRows}</tbody></table>
                  </body></html>`);
                win.document.close();
                win.focus();
                setTimeout(() => {
                  win.print();
                  win.close();
                }, 300);
              }}
            >
              {t("common.print", "Print")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrescriptionsList;
